import {
  Record,
  String as RTString,
  Boolean as RTBoolean,
  Static,
  Array as RTArray,
  Number as RTNumber,
  Partial
} from 'runtypes';
import { DeepPartial } from 'utility-types';
import { buildService } from './decorated-service';
import { PartialStateStore } from '../../../core/types/application-state';
import config from '../../../config';
import { ServiceError } from '.';

const MutualFundFormService = buildService(
  '/nasu-svc/service/mutual-fund-transfer-form'
);

const MutualFundFormServiceArgs = Record({
  custodian: Partial({
    custodianID: RTString,
    custodianName: RTString,
    address: Partial({
      street1: RTString,
      state: RTString,
      city: RTString,
      country: RTString,
      zip: RTString
    }),
    phone: RTString,
    holdings: RTArray(
      Partial({
        fundName: RTString,
        accountNumber: RTString,
        fundingPortion: RTString,
        amount: RTNumber
      })
    ),
    preliquidatedAssets: RTBoolean
  }),
  investmentAllocations: RTArray(
    Partial({
      fundName: RTString,
      fundingAmountPercentage: RTNumber
    })
  ),
  sourceAccountType: RTString,
  destinationAccountType: RTString,
  customer: Partial({
    firstName: RTString,
    middleName: RTString,
    lastName: RTString,
    taxID: RTString
  })
});

export type StaticGetMutualFundFormArgs = Static<
  typeof MutualFundFormServiceArgs
>;

export type DeepPartialMutualFundFormArgs = DeepPartial<
  StaticGetMutualFundFormArgs
>;

export const postMutualFundFormData = (
  args: DeepPartialMutualFundFormArgs
): Promise<any> => {
  return MutualFundFormService.add(args, {
    responseType: 'arraybuffer',
    headers: {
      'content-type': 'application/json',
      'content-disposition': 'inline',
      apikey: config('apiKey'),
      'x-trp-requested-with': 'XMLHttpRequest'
    },
    transformResponse: resp => resp.blob()
  });
};

export const getMutualFundBlankForm = (): Promise<any> => {
  return MutualFundFormService.index({
    responseType: 'arraybuffer',
    headers: {
      'content-type': 'application/json',
      'content-disposition': 'inline',
      apikey: config('apiKey'),
      'x-trp-requested-with': 'XMLHttpRequest'
    },
    transformResponse: resp => resp.blob()
  });
};

const MutualFundTrackingID = buildService(
  '/new-account-svc/service/account/mutualfund-transfer-ira/tracking-result'
);

const handleTrackingIdServiceResponse = (
  resp: any,
  payload: any
): Promise<any> | void => {
  if (resp?.status === 204) {
    return resp.status;
  }

  throw new ServiceError(
    `A service call has failed with status: ${
      resp.status
    } (payload: ${JSON.stringify(payload)})`
  );
};

export const getMFTransferTrackingId = (
  store: PartialStateStore
): Promise<any> => {
  const errorCodes = [204, 403, 500, 503];
  return MutualFundTrackingID.view(store?.app?.profileApplicationID, {
    handleServiceResponse: handleTrackingIdServiceResponse,
    errorCodes
  });
};
